import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  Award, 
  MapPin, 
  CloudRain, 
  CheckCircle,
  Clock,
  DollarSign
} from 'lucide-react';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';

interface RentDetailsDisplayProps {
  pilotRequirements?: {
    minimumHours?: number;
    minimumHelicopterHours?: number;
    typeRatingRequired?: boolean;
    recentExperience?: string;
  };
  insuranceRequirements?: {
    liabilityMin?: number;
    hullMin?: number;
  };
  geographicRestrictions?: string;
  weatherMinimums?: string;
  checkoutRequirements?: string;
  hourlyRate?: number;
  dailyRate?: number;
  securityDeposit?: number;
  minimumRentalPeriod?: string;
  hidePrice?: boolean;
  onRequestInfo?: () => void;
  className?: string;
}

const RentDetailsDisplay: React.FC<RentDetailsDisplayProps> = ({
  pilotRequirements,
  insuranceRequirements,
  geographicRestrictions,
  weatherMinimums,
  checkoutRequirements,
  hourlyRate,
  dailyRate,
  securityDeposit,
  minimumRentalPeriod,
  hidePrice = false,
  onRequestInfo,
  className = ''
}) => {
  // If no rental-specific data, don't render
  const hasData = pilotRequirements || insuranceRequirements || geographicRestrictions || 
                   weatherMinimums || checkoutRequirements || hourlyRate || dailyRate;
  
  if (!hasData) return null;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Pilot Requirements */}
      {pilotRequirements && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="w-5 h-5 text-primary" />
              Pilot Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pilotRequirements.minimumHours && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground">Min Total Hours</div>
                    <div className="font-semibold">{pilotRequirements.minimumHours} hrs</div>
                  </div>
                </div>
              )}
              {pilotRequirements.minimumHelicopterHours && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground">Min Helicopter Hours</div>
                    <div className="font-semibold">{pilotRequirements.minimumHelicopterHours} hrs</div>
                  </div>
                </div>
              )}
              {pilotRequirements.typeRatingRequired !== undefined && (
                <div className="col-span-2">
                  <Badge variant={pilotRequirements.typeRatingRequired ? "default" : "secondary"}>
                    {pilotRequirements.typeRatingRequired ? "Type Rating Required" : "No Type Rating Required"}
                  </Badge>
                </div>
              )}
              {pilotRequirements.recentExperience && (
                <div className="col-span-2">
                  <div className="text-xs text-muted-foreground mb-1">Recent Experience</div>
                  <p className="text-sm">{pilotRequirements.recentExperience}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insurance Requirements */}
      {insuranceRequirements && (insuranceRequirements.liabilityMin || insuranceRequirements.hullMin) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5 text-primary" />
              Insurance Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {insuranceRequirements.liabilityMin && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Liability Coverage</div>
                  <div className="font-semibold text-lg">
                    ${(insuranceRequirements.liabilityMin / 1000000).toFixed(1)}M
                  </div>
                </div>
              )}
              {insuranceRequirements.hullMin && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Hull Coverage</div>
                  <div className="font-semibold text-lg">
                    ${(insuranceRequirements.hullMin / 1000000).toFixed(1)}M
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Geographic & Weather Restrictions */}
      {(geographicRestrictions || weatherMinimums) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-primary" />
              Operational Restrictions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {geographicRestrictions && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Geographic Restrictions</span>
                </div>
                <p className="text-sm text-muted-foreground ml-6">{geographicRestrictions}</p>
              </div>
            )}
            {weatherMinimums && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <CloudRain className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Weather Minimums</span>
                </div>
                <p className="text-sm text-muted-foreground ml-6">{weatherMinimums}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Checkout Requirements */}
      {checkoutRequirements && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="w-5 h-5 text-primary" />
              Checkout Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{checkoutRequirements}</p>
          </CardContent>
        </Card>
      )}

      {/* Rates Summary */}
      {(hourlyRate || dailyRate || securityDeposit) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="w-5 h-5 text-primary" />
              Rental Rates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hidePrice ? (
              <BlurredPrivateData
                placeholderContent={
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Hourly Rate</div>
                      <div className="font-semibold text-lg">$X,XXX</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Daily Rate</div>
                      <div className="font-semibold text-lg">$XX,XXX</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Deposit</div>
                      <div className="font-semibold text-lg">$XX,XXX</div>
                    </div>
                  </div>
                }
                fieldName="Rental Rates"
                size="lg"
                onRequestInfo={onRequestInfo}
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {hourlyRate && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Hourly Rate</div>
                    <div className="font-semibold text-lg">${hourlyRate.toLocaleString()}</div>
                  </div>
                )}
                {dailyRate && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Daily Rate</div>
                    <div className="font-semibold text-lg">${dailyRate.toLocaleString()}</div>
                  </div>
                )}
                {securityDeposit && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Security Deposit</div>
                    <div className="font-semibold text-lg">${securityDeposit.toLocaleString()}</div>
                  </div>
                )}
                {minimumRentalPeriod && (
                  <div className="sm:col-span-2 md:col-span-3">
                    <Badge variant="secondary">
                      Minimum: {minimumRentalPeriod}
                    </Badge>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RentDetailsDisplay;